import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { selectApkData, selectLoading, fetchApkBackups } from '../store/slices/mobileBillingAppSlice';
import MobilebillingappDialog from './MobilebillingappDialog';
import ShareApkDialog from './ShareApkDialog';
// Who-am-I helpers — shared with the Notifications page, which uses the same GSPOS /
// own-client rule for what a user may see and author.
import { isSuperAdmin, getOwnClientId } from './notificationScope';
import { APK_STATUSES, normalizeStatus, statusTone, countByStatus, hasDownload, formatDisplayDate } from './apkStatus';
import { getClientsList, getBranches, saveApkBackup, getApkDownloadUrl } from '../utilities/api';
import './Mobilebillingapp.css';

/** Rounded status chip, matching the pills on the Notifications page. */
const Pill = ({ tone, children }) => <span className={`mba-pill mba-pill-${tone}`}>{children}</span>;

const Dash = () => <span className="mba-muted-cell">—</span>;

const MobilebillingappContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const apkData = useSelector(selectApkData);
    const loading = useSelector(selectLoading);

    const superAdmin = isSuperAdmin();
    const ownClientId = getOwnClientId();

    const [statusFilter, setStatusFilter] = useState('Active');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [editApk, setEditApk] = useState(null);
    const [shareApk, setShareApk] = useState(null);

    // Client/branch filters. A non-GSPOS user is pinned to its own client and cannot
    // widen the filter — the server enforces the same thing.
    const [clientFilter, setClientFilter] = useState(superAdmin ? null : ownClientId || null);
    const [branchFilter, setBranchFilter] = useState(null);
    const [clientOptions, setClientOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);

    const reload = useCallback(() => {
        const params = {};
        // Fall back to '0' so a non-GSPOS user with no client on record matches nothing
        // rather than everything.
        if (superAdmin) {
            if (clientFilter) params.client_id = clientFilter;
        } else {
            params.client_id = ownClientId || '0';
        }
        if (branchFilter) params.branch_id = branchFilter;
        dispatch(fetchApkBackups(params));
    }, [dispatch, superAdmin, ownClientId, clientFilter, branchFilter]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        let cancelled = false;
        getClientsList()
            .then((rows) => {
                if (cancelled) return;
                setClientOptions(rows.filter((c) => superAdmin || String(c.client_id) === ownClientId).map((c) => ({ label: c.client_name, value: String(c.client_id) })));
            })
            .catch(() => !cancelled && setClientOptions([]));
        return () => {
            cancelled = true;
        };
    }, [superAdmin, ownClientId]);

    // Branch options follow the selected client.
    useEffect(() => {
        if (!clientFilter) {
            setBranchOptions([]);
            return undefined;
        }
        let cancelled = false;
        getBranches(clientFilter)
            .then((rows) => {
                if (cancelled) return;
                setBranchOptions(rows.map((b) => ({ label: b.branch_name, value: String(b.branch_id) })));
            })
            .catch(() => !cancelled && setBranchOptions([]));
        return () => {
            cancelled = true;
        };
    }, [clientFilter]);

    const statusCounts = useMemo(() => Object.fromEntries(APK_STATUSES.map((s) => [s.value, countByStatus(apkData, s.value)])), [apkData]);

    const filteredData = useMemo(() => apkData.filter((item) => normalizeStatus(item.status) === statusFilter), [apkData, statusFilter]);

    const handleSaveRequest = (apk) => {
        const isEdit = !!apk.apkid;
        confirmDialog({
            message: isEdit ? 'Are you sure you want to update this app entry?' : 'Are you sure you want to add this new app entry?',
            header: isEdit ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleConfirmedSave(apk)
        });
    };

    const handleConfirmedSave = async (apk) => {
        const isEdit = !!apk.apkid;
        try {
            const data = await saveApkBackup(apk);
            // The API answers 200 with status:false for "nothing changed" — don't call that a success.
            if (data?.status === false) {
                toast.current.show({ severity: 'warn', summary: 'Not Saved', detail: data?.message || 'Nothing changed', life: 4000 });
                return;
            }
            toast.current.show({ severity: 'success', summary: 'Success', detail: data?.message || (isEdit ? 'App entry updated' : 'App entry added'), life: 3000 });
            setDisplayDialog(false);
            setEditApk(null);
            reload();
        } catch (err) {
            const detail = err?.response?.data?.message || err?.message || 'Save failed. Please try again.';
            toast.current.show({ severity: 'error', summary: 'Error', detail, life: 4000 });
        }
    };

    /**
     * Direct download — the backend redirects to the resolved Google Drive file (or the
     * Play Store listing when no APK is archived), so a new tab is all this needs.
     */
    const handleDownload = (rowData) => {
        if (!hasDownload(rowData)) {
            toast.current.show({ severity: 'warn', summary: 'No Link', detail: 'This entry has no Play Store or Google Drive link yet.', life: 4000 });
            return;
        }
        window.open(getApkDownloadUrl(rowData.apkid), '_blank', 'noopener');
    };

    const branchBodyTemplate = (rowData) => (rowData.branch_id ? rowData.branch_name || rowData.branch_id : <Pill tone="secondary">All Branches</Pill>);

    /** Compact "Open" chip — the raw URL is long and adds nothing to the row. */
    const linkBodyTemplate = (url, label, icon) => {
        const href = String(url || '').trim();
        if (!href) return <Dash />;
        return (
            <a className="mba-link" href={href} target="_blank" rel="noopener noreferrer" title={href} onClick={(e) => e.stopPropagation()}>
                <i className={icon} /> {label}
            </a>
        );
    };

    const statusBodyTemplate = (rowData) => <Pill tone={statusTone(rowData.status)}>{normalizeStatus(rowData.status)}</Pill>;

    const actionBodyTemplate = (rowData) => (
        <div className="mba-row-actions">
            <Button
                icon="pi pi-download"
                rounded
                text
                className="p-button-success"
                disabled={!hasDownload(rowData)}
                onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(rowData);
                }}
                tooltip="Download APK"
                tooltipOptions={{ position: 'top' }}
            />
            <Button
                icon="pi pi-share-alt"
                rounded
                text
                className="p-button-info"
                disabled={!hasDownload(rowData)}
                onClick={(e) => {
                    e.stopPropagation();
                    setShareApk(rowData);
                }}
                tooltip="Share by email"
                tooltipOptions={{ position: 'top' }}
            />
            {superAdmin && (
                <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    className="p-button-warning"
                    onClick={(e) => {
                        e.stopPropagation();
                        setEditApk(rowData);
                        setDisplayDialog(true);
                    }}
                    tooltip="Edit"
                    tooltipOptions={{ position: 'top' }}
                />
            )}
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* ── Filters ─────────────────────────────────────────── */}
            <div className="mba-card mba-toolbar">
                <div className="mba-field">
                    <label className="mba-field-label">
                        <i className="pi pi-briefcase" /> Client {!superAdmin && <span className="mba-muted">(locked to yours)</span>}
                    </label>
                    <Dropdown
                        value={clientFilter}
                        options={clientOptions}
                        onChange={(e) => {
                            setClientFilter(e.value);
                            // A branch from the previous client would filter everything out.
                            setBranchFilter(null);
                        }}
                        placeholder={superAdmin ? 'All Clients' : 'Your Client'}
                        filter={superAdmin}
                        showClear={superAdmin}
                        disabled={!superAdmin}
                        className="w-full"
                    />
                </div>

                <div className="mba-field">
                    <label className="mba-field-label">
                        <i className="pi pi-building" /> Branch
                    </label>
                    <Dropdown
                        value={branchFilter}
                        options={branchOptions}
                        onChange={(e) => setBranchFilter(e.value)}
                        placeholder={clientFilter ? 'All Branches' : 'Select a client first'}
                        filter
                        showClear
                        disabled={!clientFilter}
                        className="w-full"
                    />
                </div>

                <div className="mba-field">
                    <label className="mba-field-label">
                        <i className="pi pi-search" /> Search
                    </label>
                    <InputText
                        value={globalFilterValue}
                        onChange={(e) => {
                            setGlobalFilterValue(e.target.value);
                            setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                        }}
                        placeholder="Client, branch, version, link…"
                        className="w-full"
                    />
                </div>

                {superAdmin && (clientFilter || branchFilter) && (
                    <Button
                        label="Clear"
                        icon="pi pi-filter-slash"
                        className="mba-clear"
                        onClick={() => {
                            setClientFilter(null);
                            setBranchFilter(null);
                        }}
                    />
                )}

                {superAdmin && (
                    <Button
                        label="Add App"
                        icon="pi pi-plus"
                        className="mba-add"
                        onClick={() => {
                            setEditApk(null);
                            setDisplayDialog(true);
                        }}
                    />
                )}
            </div>

            {/* ── List ────────────────────────────────────────────── */}
            <div className="mba-card">
                <div className="mba-card-head">
                    <div>
                        <h3 className="mba-card-title">
                            <i className="pi pi-list" /> App List
                        </h3>
                        <p className="mba-card-sub">Download a build directly, or share its install links by email</p>
                    </div>
                    <div className="mba-tabs">
                        {APK_STATUSES.map((s) => (
                            <button key={s.value} className={`mba-tab${statusFilter === s.value ? ' selected' : ''}`} onClick={() => setStatusFilter(s.value)}>
                                <i className={s.icon} />
                                {s.label} <span className="mba-tab-count">{statusCounts[s.value] || 0}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="mba-loading">
                        <ProgressSpinner style={{ width: '46px', height: '46px' }} strokeWidth="4" />
                    </div>
                ) : (
                    <div className="mba-table-wrap">
                        <DataTable
                            value={filteredData}
                            filters={filters}
                            globalFilterFields={['client_name', 'branch_name', 'app_version', 'play_storelink', 'googledrive_link', 'created_by']}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                            currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                            tableStyle={{ minWidth: '62rem' }}
                            className="p-datatable-sm mba-table"
                            emptyMessage={`No ${statusFilter.toLowerCase()} app entries found`}
                            dataKey="apkid"
                        >
                            <Column field="apkid" header="ID" sortable style={{ width: '70px' }} />
                            <Column field="client_name" header="Client" sortable style={{ width: '170px' }} />
                            <Column field="branch_name" header="Branch" body={branchBodyTemplate} sortable style={{ width: '170px' }} />
                            <Column field="app_version" header="Version" body={(row) => row.app_version || <Dash />} sortable style={{ width: '110px' }} />
                            <Column field="play_storelink" header="Play Store" body={(row) => linkBodyTemplate(row.play_storelink, 'Play Store', 'pi pi-google')} style={{ width: '150px' }} />
                            <Column field="googledrive_link" header="Google Drive" body={(row) => linkBodyTemplate(row.googledrive_link, 'Drive', 'pi pi-cloud')} style={{ width: '150px' }} />
                            <Column field="updated" header="Updated" body={(row) => formatDisplayDate(row.updated) || <Dash />} sortable style={{ width: '155px' }} />
                            <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ width: '110px', textAlign: 'center' }} />
                            <Column header="Actions" body={actionBodyTemplate} style={{ width: '150px', textAlign: 'center' }} />
                        </DataTable>
                    </div>
                )}
            </div>

            <MobilebillingappDialog
                visible={displayDialog}
                apkData={editApk}
                onHide={() => {
                    setDisplayDialog(false);
                    setEditApk(null);
                }}
                onSave={handleSaveRequest}
            />

            <ShareApkDialog visible={!!shareApk} apkData={shareApk} onHide={() => setShareApk(null)} toast={toast} />
        </>
    );
};

export default MobilebillingappContent;
